import { Box, IconButton, Typography } from '@mui/material';

import CloseGlyph from '../icons/CloseGlyph';
import EditGlyph from '../icons/EditGlyph';
import MinimizeGlyph from '../icons/MinimizeGlyph';
import useStyles from './PanelHeader.styles';

export type PanelHeaderProps = {
  /** Localized title shown at the top of the Sidekick panel. */
  title: string;
  /**
   * Localized accessible name for the dismiss button. Should read as
   * "minimize" under M1, where the drawer collapses back into the
   * dock, and as "close" otherwise.
   */
  dismissAriaLabel: string;
  /** Localized accessible name for the "new conversation" edit button. */
  newConversationAriaLabel: string;
  /** Fired when the user activates the close affordance. */
  onClose: () => void;
  /** Fired when the user activates the "new conversation" affordance. */
  onNewConversation: () => void;
  /**
   * Whether the new-conversation edit button should render in its
   * pressed/active state (brand-100 circular fill, brand-600 ink).
   * The Sidekick panel passes `true` while the in-chat reset
   * confirmation prompt is open so the toggle stays visually
   * anchored. Also surfaces as `aria-pressed` for assistive tech.
   * Defaults to `false`.
   */
  isNewConversationActive?: boolean;
  /**
   * Whether the new-conversation edit button should be disabled.
   * The Sidekick panel passes `true` while the in-chat reset is
   * mid-confirmation (between Continue click and reset dispatch) so
   * the button cannot unmount the prompt and silently cancel the
   * pending reset. Defaults to `false`.
   */
  isNewConversationDisabled?: boolean;
  /** Whether the M1 visual treatment is active. */
  isM1Enabled?: boolean;
};

/**
 * Top bar of the AI Sidekick drawer: panel title, a new-conversation
 * edit button, and a dismiss button. Stateless — orchestration lives
 * in `AISidekickPanel`.
 *
 * Under M1 the drawer is paired with a dock it collapses back into, so
 * the dismiss button reads as a minimize. Without a dock there is
 * nowhere to collapse to, and it stays a close.
 */
function PanelHeader({
  title,
  dismissAriaLabel,
  newConversationAriaLabel,
  onClose,
  onNewConversation,
  isNewConversationActive = false,
  isNewConversationDisabled = false,
  isM1Enabled = false,
}: PanelHeaderProps) {
  const { classes } = useStyles({ isM1Enabled });

  return (
    <Box className={classes.header} data-testid="ai-sidekick-panel-header">
      <Typography className={classes.title} component="h2">
        {title}
      </Typography>
      <Box className={classes.actions}>
        <IconButton
          className={classes.iconButton}
          aria-label={newConversationAriaLabel}
          aria-pressed={isNewConversationActive}
          data-active={isNewConversationActive ? 'true' : 'false'}
          onClick={onNewConversation}
          disabled={isNewConversationDisabled}
          data-testid="ai-sidekick-panel-new-conversation"
        >
          <EditGlyph />
        </IconButton>
        <IconButton
          className={classes.iconButton}
          aria-label={dismissAriaLabel}
          onClick={onClose}
          data-testid="ai-sidekick-panel-close"
        >
          {isM1Enabled ? <MinimizeGlyph /> : <CloseGlyph />}
        </IconButton>
      </Box>
    </Box>
  );
}

export default PanelHeader;
